import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { AppAction, AppContext } from "helpers/context";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { upgradeRoleQuotasPermission, queryQuotasIdsByRoleId } from "../../../../api/layer";
import { RoleType } from "../type";
import LimitControl from "./limit-control/limit-control";
import styles from "./role-quota-modal.module.scss";

interface Props {
	onClose: () => void;
	data?: RoleType;
}

type LimitOption = "unlimited" | "limited";

interface QuotaState {
	limitOption: LimitOption;
	limitCount: number;
}

const QUOTA_ITEMS = [
	{ quotasId: "103108", labelKey: "permissionConfig.fathomAnalysisLocationsCount" },
	{ quotasId: "103107", labelKey: "permissionConfig.hazardAnalysisLocationsCount" },
	{ quotasId: "103109", labelKey: "permissionConfig.crsAnalysisLocationsCount" },
	{ quotasId: "106104", labelKey: "permissionConfig.lineFathomAnalysisCount" },
	{ quotasId: "106103", labelKey: "permissionConfig.lineNotFathomAnalysisCount" },
	{ quotasId: "107104", labelKey: "permissionConfig.polygonFathomAnalysisCount" },
	{ quotasId: "107103", labelKey: "permissionConfig.polygonNotFathomAnalysisCount" },
	{ quotasId: "104004", labelKey: "permissionConfig.eventsAnalysisLocationsCount" },
	{ quotasId: "103101", labelKey: "permissionConfig.addressCount" }
];

const getDefaultQuotaState = (): Record<string, QuotaState> =>
	QUOTA_ITEMS.reduce((acc, item) => {
		acc[item.quotasId] = {
			limitOption: "unlimited",
			limitCount: 0
		};
		return acc;
	}, {} as Record<string, QuotaState>);

const RoleQuotaModal: React.FC<Props> = (props) => {
	const [, dispatch] = useContext(AppContext);

	const [t] = useTranslation();
	const [quotaState, setQuotaState] = useState<Record<string, QuotaState>>(getDefaultQuotaState);

	const [loading, setLoading] = useState<boolean>(false);

	const initData = () => {
		if (!props.data || !props.data.id) {
			return;
		}

		const roleId = props.data.id;
		if (!roleId) {
			return;
		}

		setLoading(true);

		queryQuotasIdsByRoleId({ roleId })
			.then((res) => {
				const quotas = res?.data || [];
				const nextState = getDefaultQuotaState();

				QUOTA_ITEMS.forEach((item) => {
					const quota = quotas.find((q: any) => q.quotasId === item.quotasId);
					const quantity = quota?.quantity;
					nextState[item.quotasId] = {
						limitOption: quantity !== undefined && quantity !== -1 ? "limited" : "unlimited",
						limitCount: quantity !== undefined && quantity !== -1 ? quantity : 0
					};
				});

				setQuotaState(nextState);
			})
			.catch((error) => {
				console.log(error);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleSave = () => {
		if (!props.data || !props.data.id) {
			return;
		}

		const roleId = props.data.id;
		if (!roleId) {
			return;
		}

		const quotasList = QUOTA_ITEMS.map((item) => {
			const current = quotaState[item.quotasId] || {
				limitOption: "unlimited" as LimitOption,
				limitCount: 0
			};
			return {
				quantity: current.limitOption === "unlimited" ? -1 : current.limitCount,
				quotasId: item.quotasId
			};
		});

		const param = {
			quotasList,
			roleId,
		};

		setLoading(true);

		upgradeRoleQuotasPermission(param)
			.then((resp: any) => {
				if (!resp || resp.code !== 200) {
					dispatch({
						type: AppAction.addSnackBarParams,
						payload: {
							title: "updateQuota",
							titleIconColor: "yellow",
							titleIcon: "warning",
							resMessage: resp.message,
						},
					});
					return;
				}
				dispatch({
					type: AppAction.addSnackBarParams,
					payload: {
						title: "updateQuota",
						message: "updateQuotaSuccess",
					},
				});
			})
			.catch(() => {
				dispatch({
					type: AppAction.addSnackBarParams,
					payload: {
						title: "updateQuota",
						titleIconColor: "yellow",
						titleIcon: "warning",
						message: "updateQuotaError",
					},
				});
			})
			.finally(() => {
				props.onClose();
				setLoading(false);
			});
	};

	useEffect(() => {
		initData();
	}, [props.data]);

	return (
		<Dialog
			open={!!props.data}
			onClose={() => props.onClose()}
		>
			<DialogTitle
				sx={{
					fontWeight: 600,
					fontSize: "16px",
					opacity: 0.79,
					color: "#333",
					width: "600px",
				}}
			>
				{props.data && props.data.roleName}
			</DialogTitle>
			{loading ? (
				<div className={styles.loading}>
					<div className="loading loading-spinner loading-lg"></div>
					<div>{t("layers.loading")}</div>
				</div>
			) : (
				<DialogContent
					sx={{
						overflowY: "auto",
						"&::-webkit-scrollbar": { width: "8px" },
						"&::-webkit-scrollbar-thumb": { backgroundColor: "#888", borderRadius: "4px" },
						"&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
						"&::-webkit-scrollbar-track": { background: "#f1f1f1" },
					}}
				>
					<div className={styles.section}>
						<Typography style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "10px" }}>{t("permissionConfig.title1")}</Typography>
						<div style={{ paddingLeft: "20px" }}>
							{QUOTA_ITEMS.map((item) => {
								const current = quotaState[item.quotasId] || {
									limitOption: "unlimited" as LimitOption,
									limitCount: 0
								};

								return (
									<div key={item.quotasId}>
										<Typography style={{ fontSize: "14px", color: "#333" }}>{t(item.labelKey)}</Typography>
										<LimitControl
											initialLimitOption={current.limitOption}
											initialLimitCount={current.limitCount}
											onLimitChange={(nextLimitOption) => {
												setQuotaState((prev) => ({
													...prev,
													[item.quotasId]: {
														...(prev[item.quotasId] || {
															limitCount: 0,
															limitOption: "unlimited"
														}),
														limitOption: nextLimitOption as LimitOption
													}
												}));
											}}
											onCountChange={(nextLimitCount) => {
												setQuotaState((prev) => ({
													...prev,
													[item.quotasId]: {
														...(prev[item.quotasId] || {
															limitCount: 0,
															limitOption: "unlimited"
														}),
														limitCount: nextLimitCount
													}
												}));
											}}
										/>
									</div>
								);
							})}
						</div>
					</div>
				</DialogContent>
			)}
			<DialogActions style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
				<Button
					onClick={initData}
					color="success"
				>
					{t("buttons.reset")}
				</Button>

				<div style={{ marginLeft: "auto" }}>
					<Button
						onClick={() => props.onClose()}
						color="success"
					>
						{t("buttons.cancel")}
					</Button>
					<Button
						onClick={handleSave}
						color="success"
						variant="contained"
					>
						{t("buttons.save")}
					</Button>
				</div>
			</DialogActions>
		</Dialog>
	);
};

export default RoleQuotaModal;
